import React, { useRef, useState } from 'react';

import ButtonGroup from '@material-ui/core/ButtonGroup';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popover from '@material-ui/core/Popover';
import makeStyles from '@material-ui/styles/makeStyles';

import { UiButton } from './ui-button';

import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';

export type SplitButtonRenderProps = {
  open?: boolean;
  disabled?: boolean;
  openClass?: string;
};

type Props = {
  disabled?: boolean;
  className?: string;
  buttonGroupClassName?: string;
  MainButton: (props: SplitButtonRenderProps) => JSX.Element;
  DropdownButtons: (props: SplitButtonRenderProps) => JSX.Element;
  popperButtonClassName?: string;
  popperButtonVariant?: 'primary' | 'secondary' | 'tertiary' | 'dismiss' | 'contained';
  popperButtonSize?: 'default' | 'xxs' | 'xs' | 'sm' | 'lg';
  popperClassName?: string;
  dropdownFontSize?: 'xs' | 'sm' | 'md' | 'lg';
  dataCy?: string;
};

const useStyles = makeStyles(
  // MuiPaper (which is used in the dropdown) only supports border radii on all
  // corners. we want the dropdown to only have bottom radii, so we override
  // the class itself.
  {
    rounded: {
      borderTopRightRadius: '0px !important',
      borderTopLeftRadius: '0px !important',
    },
  },
  { name: 'MuiPaper' },
);

// we need to wrap this in its own component to synchronize state updates
// which become apparent when disabled state changes
const MainButtonWithDropdown = ({
  MainButton,
  open,
  disabled,
  handleOpenButtonGroup,
  dropdownFontSize,
  popperButtonSize,
  popperButtonVariant,
  popperButtonClassName,
}: Props & SplitButtonRenderProps & { handleOpenButtonGroup: () => void }) => {
  const mainButtonOpenClass = 'u-borderRadius-bottom-right-none u-borderRadius-bottom-left-none';

  return (
    <>
      <MainButton open={open} openClass={open ? mainButtonOpenClass : ''} disabled={disabled} />
      <UiButton
        // unset popper button's bottom right border if the dropdown is open
        className={`u-boxShadow-none u-borderRadius-left-none ${
          open ? 'u-borderRadius-bottom-right-none' : ''
        } ${popperButtonClassName || ''}`}
        variant={popperButtonVariant}
        // NOTE: the ClickAwayListener wrapping the list buttons should listen
        // to an event that fires when this button is clicked and the button
        // group is open, so we only attach an open handler to this event
        onClick={handleOpenButtonGroup}
        size={popperButtonSize}
        disabled={disabled}
        dataCy="expand-dropdown"
      >
        <MaterialDesignIcon iconName={ICON_NAMES.ARROW_DROP_DOWN} size={dropdownFontSize || 'md'} />
      </UiButton>
    </>
  );
};

export const UiSplitButton = (props: Props) => {
  const { disabled, buttonGroupClassName, DropdownButtons, popperClassName, dataCy } = props;
  const classes = useStyles();

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const handleOpenButtonGroup = () => {
    setOpen(true);
  };

  const handleCloseButtonGroup = () => {
    setOpen(false);
  };

  return (
    <>
      <ButtonGroup
        className={buttonGroupClassName}
        variant="contained"
        ref={anchorRef}
        size="small"
        data-cy={dataCy}
      >
        <MainButtonWithDropdown
          handleOpenButtonGroup={handleOpenButtonGroup}
          open={open}
          {...props}
        />
      </ButtonGroup>
      <Popover
        // ensure the dropdown appears above other elements
        className={`${classes.rounded} u-position-top-sticky ${popperClassName ?? ''}`}
        open={open}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Paper
          // set the dropdown's width to that of the main button
          style={{
            ...(anchorRef?.current?.offsetWidth
              ? {
                  minWidth: anchorRef.current.offsetWidth,
                }
              : {}),
          }}
        >
          <ClickAwayListener onClickAway={handleCloseButtonGroup}>
            <MenuList
              className="u-padding-none"
              // close the button group even when a list button is clicked
              onClick={handleCloseButtonGroup}
            >
              <DropdownButtons open={open} disabled={disabled} />
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popover>
    </>
  );
};
